import { FormFieldType } from '../../types/form/form-fields';
import type {
	DateTimeValue,
	DateValue,
	LocalTimeValue,
	NumberValue,
	OptionsValue,
	TextValue,
} from '../../types/form/form-fields/field-values';
import {
	isDateTimeValue,
	isDateValue,
	isLocalTimeValue,
	isNumberValue,
	isOptionsValue,
	isTextValue,
} from '../../types/form/form-fields/field-values/utils';
import { FormSectionConditionCheckType } from '../../types/form/form-section';
import type { TypeToCheckConfig, TypeToCheckConfigs } from '../../types/form/form-section/config';
import {
	dateValueBetweenCheck,
	dateValueDoesNotEqualToCheck,
	dateValueEmptyCheck,
	dateValueEqualToCheck,
	dateValueGreaterThanCheck,
	dateValueGreaterThanOrEqualToCheck,
	dateValueLessThanCheck,
	dateValueLessThanOrEqualToCheck,
	dateValueNotEmptyCheck,
} from './constraints/dateConstraint';
import {
	dateTimeValueBetweenCheck,
	dateTimeValueDoesNotEqualToCheck,
	dateTimeValueEmptyCheck,
	dateTimeValueEqualToCheck,
	dateTimeValueGreaterThanCheck,
	dateTimeValueGreaterThanOrEqualToCheck,
	dateTimeValueLessThanCheck,
	dateTimeValueLessThanOrEqualToCheck,
	dateTimeValueNotEmptyCheck,
} from './constraints/dateTimeConstraint';
import {
	numberValueBetweenCheck,
	numberValueDoesNotEqualToCheck,
	numberValueEmptyCheck,
	numberValueEqualToCheck,
	numberValueGreaterThanCheck,
	numberValueGreaterThanOrEqualToCheck,
	numberValueLessThanCheck,
	numberValueLessThanOrEqualToCheck,
	numberValueNotEmptyCheck,
} from './constraints/numberConstraint';
import {
	cascadingValueNoneOfCheck,
	cascadingValueSomeOfCheck,
	optionsValueEmptyCheck,
	optionsValueEqualsCheck,
	optionsValueNoneOfCheck,
	optionsValueNotEmptyCheck,
	optionsValueSomeOfCheck,
} from './constraints/optionConstraints';
import {
	textValueContainsCheck,
	textValueDoesNotContainCheck,
	textValueDoesNotEqualCheck,
	textValueEmptyCheck,
	textValueEqualToCheck,
	textValueNotEmptyCheck,
} from './constraints/textConstraint';
import {
	timeValueBetweenCheck,
	timeValueDoesNotEqualToCheck,
	timeValueEmptyCheck,
	timeValueEqualToCheck,
	timeValueGreaterThanCheck,
	timeValueGreaterThanOrEqualToCheck,
	timeValueLessThanCheck,
	timeValueLessThanOrEqualToCheck,
	timeValueNotEmptyCheck,
} from './constraints/timeConstraint';

const basicOptionsConfig: TypeToCheckConfig<OptionsValue> = {
	typeGuard: isOptionsValue,
	constraintChecks: {
		[FormSectionConditionCheckType.NONE_OF]: optionsValueNoneOfCheck,
		[FormSectionConditionCheckType.SOME_OF]: optionsValueSomeOfCheck,
		[FormSectionConditionCheckType.EMPTY]: optionsValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: optionsValueNotEmptyCheck,
	},
};

const extendedOptionsConfig: TypeToCheckConfig<OptionsValue> = {
	typeGuard: isOptionsValue,
	constraintChecks: {
		...basicOptionsConfig.constraintChecks,
		[FormSectionConditionCheckType.ALL_OF]: optionsValueEqualsCheck,
		[FormSectionConditionCheckType.EMPTY]: optionsValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: optionsValueNotEmptyCheck,
	},
};

const textConfig: TypeToCheckConfig<TextValue> = {
	typeGuard: isTextValue,
	constraintChecks: {
		[FormSectionConditionCheckType.CONTAINS]: textValueContainsCheck,
		[FormSectionConditionCheckType.DOES_NOT_CONTAIN]: textValueDoesNotContainCheck,
		[FormSectionConditionCheckType.DOES_NOT_EQUAL]: textValueDoesNotEqualCheck,
		[FormSectionConditionCheckType.EQUAL_TO]: textValueEqualToCheck,
		[FormSectionConditionCheckType.EMPTY]: textValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: textValueNotEmptyCheck,
	},
};

const dateConfig: TypeToCheckConfig<DateValue> = {
	typeGuard: isDateValue,
	constraintChecks: {
		[FormSectionConditionCheckType.EQUAL_TO]: dateValueEqualToCheck,
		[FormSectionConditionCheckType.DOES_NOT_EQUAL]: dateValueDoesNotEqualToCheck,
		[FormSectionConditionCheckType.GREATER_THAN]: dateValueGreaterThanCheck,
		[FormSectionConditionCheckType.GREATER_THAN_OR_EQUAL_TO]: dateValueGreaterThanOrEqualToCheck,
		[FormSectionConditionCheckType.LESS_THAN]: dateValueLessThanCheck,
		[FormSectionConditionCheckType.LESS_THAN_OR_EQUAL_TO]: dateValueLessThanOrEqualToCheck,
		[FormSectionConditionCheckType.BETWEEN]: dateValueBetweenCheck,
		[FormSectionConditionCheckType.EMPTY]: dateValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: dateValueNotEmptyCheck,
	},
};

const dateTimeConfig: TypeToCheckConfig<DateTimeValue> = {
	typeGuard: isDateTimeValue,
	constraintChecks: {
		[FormSectionConditionCheckType.EQUAL_TO]: dateTimeValueEqualToCheck,
		[FormSectionConditionCheckType.DOES_NOT_EQUAL]: dateTimeValueDoesNotEqualToCheck,
		[FormSectionConditionCheckType.GREATER_THAN]: dateTimeValueGreaterThanCheck,
		[FormSectionConditionCheckType.GREATER_THAN_OR_EQUAL_TO]:
			dateTimeValueGreaterThanOrEqualToCheck,
		[FormSectionConditionCheckType.LESS_THAN]: dateTimeValueLessThanCheck,
		[FormSectionConditionCheckType.LESS_THAN_OR_EQUAL_TO]: dateTimeValueLessThanOrEqualToCheck,
		[FormSectionConditionCheckType.BETWEEN]: dateTimeValueBetweenCheck,
		[FormSectionConditionCheckType.EMPTY]: dateTimeValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: dateTimeValueNotEmptyCheck,
	},
};

const timeConfig: TypeToCheckConfig<LocalTimeValue> = {
	typeGuard: isLocalTimeValue,
	constraintChecks: {
		[FormSectionConditionCheckType.EQUAL_TO]: timeValueEqualToCheck,
		[FormSectionConditionCheckType.DOES_NOT_EQUAL]: timeValueDoesNotEqualToCheck,
		[FormSectionConditionCheckType.GREATER_THAN]: timeValueGreaterThanCheck,
		[FormSectionConditionCheckType.GREATER_THAN_OR_EQUAL_TO]: timeValueGreaterThanOrEqualToCheck,
		[FormSectionConditionCheckType.LESS_THAN]: timeValueLessThanCheck,
		[FormSectionConditionCheckType.LESS_THAN_OR_EQUAL_TO]: timeValueLessThanOrEqualToCheck,
		[FormSectionConditionCheckType.BETWEEN]: timeValueBetweenCheck,
		[FormSectionConditionCheckType.EMPTY]: timeValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: timeValueNotEmptyCheck,
	},
};

const numberConfig: TypeToCheckConfig<NumberValue> = {
	typeGuard: isNumberValue,
	constraintChecks: {
		[FormSectionConditionCheckType.EQUAL_TO]: numberValueEqualToCheck,
		[FormSectionConditionCheckType.DOES_NOT_EQUAL]: numberValueDoesNotEqualToCheck,
		[FormSectionConditionCheckType.GREATER_THAN]: numberValueGreaterThanCheck,
		[FormSectionConditionCheckType.GREATER_THAN_OR_EQUAL_TO]: numberValueGreaterThanOrEqualToCheck,
		[FormSectionConditionCheckType.LESS_THAN]: numberValueLessThanCheck,
		[FormSectionConditionCheckType.LESS_THAN_OR_EQUAL_TO]: numberValueLessThanOrEqualToCheck,
		[FormSectionConditionCheckType.BETWEEN]: numberValueBetweenCheck,
		[FormSectionConditionCheckType.EMPTY]: numberValueEmptyCheck,
		[FormSectionConditionCheckType.NOT_EMPTY]: numberValueNotEmptyCheck,
	},
};

export const fieldTypeToCheckConfigs: TypeToCheckConfigs = {
	[FormFieldType.CASCADING_SELECT]: {
		typeGuard: isOptionsValue,
		constraintChecks: {
			[FormSectionConditionCheckType.NONE_OF]: cascadingValueNoneOfCheck,
			[FormSectionConditionCheckType.SOME_OF]: cascadingValueSomeOfCheck,
			[FormSectionConditionCheckType.EMPTY]: optionsValueEmptyCheck,
			[FormSectionConditionCheckType.NOT_EMPTY]: optionsValueNotEmptyCheck,
		},
	},
	[FormFieldType.RADIO]: basicOptionsConfig,
	[FormFieldType.SINGLE_SELECT]: basicOptionsConfig,
	[FormFieldType.MULTIPLE_SELECT]: extendedOptionsConfig,
	[FormFieldType.CHECKBOX]: extendedOptionsConfig,
	[FormFieldType.RATING]: numberConfig,
	[FormFieldType.SCALE]: numberConfig,
	[FormFieldType.DATE]: dateConfig,
	[FormFieldType.DATE_TIME]: dateTimeConfig,
	[FormFieldType.LOCAL_TIME]: timeConfig,
	[FormFieldType.NUMBER]: numberConfig,
	[FormFieldType.LONG_TEXT]: textConfig,
	[FormFieldType.EMAIL]: textConfig,
	[FormFieldType.PARAGRAPH]: textConfig,
	[FormFieldType.SHORT_TEXT]: textConfig,
	[FormFieldType.URL]: textConfig,
};
